import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { DetectedDocumentActionMode, DocumentType } from '../../interfaces/document';

interface DetectedDocumentActionDialogProps {
  open: boolean;
  mode: DetectedDocumentActionMode | null;
  currentType: string | null;
  patientIdentifier?: string | null;
  newType: string;
  reason: string;
  crmNote: string;
  loading: boolean;
  documentTypes: DocumentType[];
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onCrmNoteChange: (note: string) => void;
  onNewTypeChange: (type: string) => void;
  onConfirm: () => void;
}

const formatTypeLabel = (typeName: string): string =>
  typeName.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const DetectedDocumentActionDialog: FC<DetectedDocumentActionDialogProps> = ({
  open,
  mode,
  currentType,
  patientIdentifier,
  newType,
  reason,
  crmNote,
  loading,
  documentTypes,
  onClose,
  onReasonChange,
  onCrmNoteChange,
  onNewTypeChange,
  onConfirm,
}) => {
  const isDiscardDetected = mode === 'discard';
  const isDiscardPatient = mode === 'discard-patient';
  const isAnyDiscard = isDiscardDetected || isDiscardPatient;
  const isChangeType = mode === 'change-type';

  const title = isDiscardPatient
    ? 'Discard Detected Patient'
    : isDiscardDetected
    ? 'Discard Detected Document'
    : 'Change Document Type';

  const confirmLabel = isAnyDiscard ? 'Discard' : 'Reclassify';

  const description = isDiscardPatient
    ? 'This discards a single detected patient and any linked referral or prior-authorization. Other patients in the same section are not affected.'
    : isDiscardDetected
    ? 'This discards the detected section along with any extracted patient, referral, or prior-authorization data linked to it. Linked referrals are marked Cancelled in our system.'
    : 'This reclassifies the detected section with the new type, discards previous extraction artifacts, and queues a fresh extraction. LLM classification is NOT re-run — your selection is trusted.';

  const availableTypes = documentTypes.filter((t) => t.name !== currentType);
  const typeMissingError = isChangeType && !newType;
  const reasonMissingError = !reason.trim();

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {description}
        </Typography>

        {isDiscardPatient && patientIdentifier && (
          <Typography variant='body2' sx={{ mb: 2 }}>
            <strong>Patient:</strong> {patientIdentifier}
          </Typography>
        )}

        {!isDiscardPatient && currentType && (
          <Typography variant='body2' sx={{ mb: 2 }}>
            <strong>Current type:</strong> {formatTypeLabel(currentType)}
          </Typography>
        )}

        {isChangeType && (
          <Autocomplete
            sx={{ mb: 2 }}
            disabled={loading}
            options={availableTypes}
            value={availableTypes.find((t) => t.name === newType) ?? null}
            onChange={(_, option) => onNewTypeChange(option?.name ?? '')}
            getOptionLabel={(t) =>
              `${formatTypeLabel(t.name)}${
                !t.isParsingEnabled ? ' (classification only)' : ''
              }`
            }
            isOptionEqualToValue={(opt, val) => opt.name === val.name}
            autoHighlight
            autoComplete
            renderInput={(params) => (
              <TextField
                {...params}
                label='New document type *'
                placeholder='Type to search…'
                error={typeMissingError}
                helperText={typeMissingError ? 'Select a type to continue.' : ''}
              />
            )}
          />
        )}

        {isAnyDiscard && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {isDiscardPatient
              ? 'Discarding this patient cannot be undone via this UI. The linked referral (if any) is marked Cancelled in our system.'
              : 'Discarding cannot be undone via this UI. If this was the only active detected section on the document, the document will be flagged as having no clinical content.'}
          </Alert>
        )}

        <TextField
          label='Reason *'
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder={
            isDiscardPatient
              ? 'Explain why this patient is being discarded (e.g. LLM hallucinated this patient)...'
              : isDiscardDetected
              ? 'Explain why this detected section is being discarded...'
              : 'Explain why the classification is being corrected...'
          }
          disabled={loading}
          error={reasonMissingError}
          helperText={reasonMissingError ? 'A reason is required.' : ''}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 500 }}
        />

        <TextField
          label='CRM note (optional)'
          value={crmNote}
          onChange={(e) => onCrmNoteChange(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder='Optional context for the leads team reviewing this correction.'
          disabled={loading}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          disabled={loading || reasonMissingError || typeMissingError}
          color={isAnyDiscard ? 'error' : 'primary'}>
          {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetectedDocumentActionDialog;
